/**
 * HALFSONE — AJAX cart drawer.
 */

class NevorelCartDrawer {
  constructor() {
    this.drawerOverlay = document.getElementById('CartDrawerOverlay');
    this.drawer = document.getElementById('CartDrawer');
    this.closeButtons = document.querySelectorAll('[data-action="close-cart-drawer"]');
    this.cartTriggers = document.querySelectorAll('[data-action="open-cart-drawer"]');
    this.cartCountBadges = document.querySelectorAll('.cart-count-badge');
    this.itemsContainer = document.getElementById('CartDrawerItems');
    this.subtotalElement = document.getElementById('CartDrawerSubtotal');
    this.emptyState = document.getElementById('CartDrawerEmpty');
    this.filledState = document.getElementById('CartDrawerFilled');
    this.legalCheckbox = document.querySelector('#CartDrawerLegalAcceptance');
    this.legalError = this.drawerOverlay?.querySelector('[data-legal-acceptance-error]');
    this.checkoutLink = this.drawerOverlay?.querySelector('[data-checkout-link]');

    this.init();
  }

  init() {
    this.cartTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });

    this.closeButtons.forEach((button) => button.addEventListener('click', () => this.close()));

    this.drawerOverlay?.addEventListener('click', (event) => {
      if (event.target === this.drawerOverlay) this.close();
    });

    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[action*="/cart/add"]');
      if (form && !form.dataset.bypassAjax) {
        event.preventDefault();
        this.addForm(form);
      }
    });

    this.itemsContainer?.addEventListener('click', (event) => {
      const target = event.target.closest('button');
      if (!target) return;

      const line = Number.parseInt(target.dataset.line, 10);
      const action = target.dataset.action;
      const currentQty = Number.parseInt(target.dataset.qty, 10);
      if (!Number.isFinite(line) || line < 1) return;

      if (action === 'decrease-qty') this.changeQuantity(line, Math.max(0, currentQty - 1));
      if (action === 'increase-qty') this.changeQuantity(line, currentQty + 1);
      if (action === 'remove-item') this.changeQuantity(line, 0);
    });

    this.legalCheckbox?.addEventListener('change', () => this.syncCheckoutGate());
    this.checkoutLink?.addEventListener('click', (event) => {
      if (this.legalCheckbox && !this.legalCheckbox.checked) {
        event.preventDefault();
        this.showLegalError();
      }
    });
    this.syncCheckoutGate();
  }

  syncCheckoutGate() {
    if (!this.checkoutLink) return;
    const accepted = !this.legalCheckbox || Boolean(this.legalCheckbox.checked);
    this.checkoutLink.classList.toggle('is-disabled', !accepted);
    this.checkoutLink.setAttribute('aria-disabled', accepted ? 'false' : 'true');
    if (accepted && this.legalError) this.legalError.textContent = '';
  }

  showLegalError() {
    if (this.legalError) this.legalError.textContent = 'Ödeme adımına geçmek için bilgilendirme ve sözleşme kutusunu işaretleyin.';
    this.legalCheckbox?.focus();
  }

  open() {
    if (!this.drawerOverlay) return;
    this.drawerOverlay.classList.add('open');
    this.drawerOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.drawer?.querySelector('[data-action="close-cart-drawer"]')?.focus();
  }

  close() {
    if (!this.drawerOverlay) return;
    this.drawerOverlay.classList.remove('open');
    this.drawerOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  async addForm(form) {
    const submitButton = form.querySelector('[type="submit"]');
    const originalText = submitButton?.innerHTML || '';
    const formMessage = form.querySelector('[data-product-form-message]');

    if (submitButton) {
      submitButton.disabled = true;
      submitButton.setAttribute('aria-busy', 'true');
        submitButton.innerHTML = '<span>Adding…</span>';
    }
    if (formMessage) formMessage.textContent = '';

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'The item could not be added.');
      }

      await this.refreshCart();
      this.open();
      window.nevorelAnnounce?.('Ürün sepete eklendi');
    } catch (error) {
      if (formMessage) formMessage.textContent = error.message;
      window.nevorelAnnounce?.(error.message);
    } finally {
      if (submitButton) {
        const currentVariant = form.querySelector('[data-variant-radio]:checked');
        const available = currentVariant ? currentVariant.dataset.available === 'true' : true;
        submitButton.disabled = !available;
        submitButton.removeAttribute('aria-busy');
        submitButton.innerHTML = originalText;
      }
    }
  }

  async changeQuantity(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      if (!response.ok) throw new Error('The bag could not be updated.');
      await this.refreshCart();
    } catch (error) {
      window.nevorelAnnounce?.(error.message);
    }
  }

  async refreshCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('The bag could not be loaded.');
    this.renderCart(await response.json());
  }

  renderCart(cart) {
    this.cartCountBadges.forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.style.display = cart.item_count > 0 ? 'inline-flex' : 'none';
    });

    if (cart.item_count === 0) {
      if (this.emptyState) this.emptyState.style.display = 'block';
      if (this.filledState) this.filledState.style.display = 'none';
      if (this.itemsContainer) this.itemsContainer.innerHTML = '';
      return;
    }

    if (this.emptyState) this.emptyState.style.display = 'none';
    if (this.filledState) this.filledState.style.display = 'flex';

    if (this.itemsContainer) {
      this.itemsContainer.innerHTML = cart.items.map((item, index) => {
        const line = index + 1;
        const price = this.formatMoney(item.final_line_price);
        const image = this.safeImageUrl(item.image);
        const itemTitle = this.escapeHtml(item.product_title || item.title || 'Item');
        const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
          ? `<span class="cart-item-variant">${this.escapeHtml(item.variant_title)}</span>`
          : '';

        return `
          <div class="cart-item" data-key="${this.escapeHtml(item.key)}">
            <div class="cart-item-image">
              ${image ? `<img src="${image}" alt="${itemTitle}" width="72" height="72" loading="lazy">` : '<div class="cart-item-placeholder" aria-hidden="true"></div>'}
            </div>
            <div class="cart-item-info">
              <span class="cart-item-title">${itemTitle}</span>
              ${variantTitle}
              <span class="cart-item-price">${price}</span>
              <div class="cart-item-actions">
                <div class="quantity-control quantity-control--compact">
                  <button type="button" class="quantity-btn" data-action="decrease-qty" data-line="${line}" data-qty="${item.quantity}" aria-label="Decrease quantity">−</button>
                  <span>${item.quantity}</span>
                  <button type="button" class="quantity-btn" data-action="increase-qty" data-line="${line}" data-qty="${item.quantity}" aria-label="Increase quantity">+</button>
                </div>
                <button type="button" data-action="remove-item" data-line="${line}" class="cart-remove-button">Remove</button>
              </div>
            </div>
          </div>`;
      }).join('');
    }

    if (this.subtotalElement) this.subtotalElement.textContent = this.formatMoney(cart.total_price);
    if (this.legalCheckbox) this.legalCheckbox.checked = false;
    this.syncCheckoutGate();
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: window.Shopify?.currency?.active || 'USD' }).format(cents / 100);
  }

  escapeHtml(value) {
    return String(value ?? '').replace(/[&<>'"]/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
    })[character]);
  }

  safeImageUrl(value) {
    if (!value) return '';
    try {
      const parsed = new URL(value, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? this.escapeHtml(parsed.href) : '';
    } catch (_) {
      return '';
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.NevorelCart = new NevorelCartDrawer();
});
